/**
 * Running sub-agent subtasks — "2 running subtasks" under the transcript.
 *
 * openclaw runs a sub-agent as its own session, linked to the session that
 * spawned it by `spawnedBy`, and pushes a `sessions.changed` for that child
 * when its run starts and when it ends. So the count is derived from pushes
 * this client already receives: no `tasks.list` poll (the gateway's docs tell
 * clients not to, and it needs an operator scope), no new subscription.
 *
 * "Subtask", not "task": the sidebar's Tasks pane is scheduled cron jobs.
 *
 * This module reads one child row and answers two questions — whose child is
 * it, and is it running — then folds the answer into a per-chat map, so a
 * background chat's subtasks stay counted while another chat is on screen.
 * Resolving a parent key to a chat is the frame handler's job. A gateway that
 * pushes no child rows produces no subtasks and no row, as today.
 */

/** One child session row, reduced to what a subtask count needs. */
export interface SubagentActivity {
  /** The child's own session key — the identity a subtask is counted under. */
  readonly key: string;
  /** The session that spawned it; null for an ordinary chat, never counted. */
  readonly parentKey: string | null;
  /**
   * Running, stopped, or null when the row says nothing either way.
   *
   * Null is not "idle". `sessions.changed` also fires for renames and pins,
   * and openclaw's protocol notes give an omitted active-run field the meaning
   * "no change", so reading it as stopped would drop a live subtask.
   */
  readonly running: boolean | null;
}

/** A running child, under the chat that spawned it. */
export interface RunningSubtask {
  /** The child's session key. */
  readonly key: string;
  /** When a push last said this child was running. See `SUBTASK_STALE_MS`. */
  readonly lastSeenAt: number;
}

/** Session id → the sub-agent subtasks running under that chat. */
export type RunningSubtasks = ReadonlyMap<string, readonly RunningSubtask[]>;

/**
 * How long a subtask stays counted without the gateway mentioning it again.
 *
 * The backstop for the one way this count can be wrong on screen: a child's
 * end push never arrives and the row says "1 running subtask" forever. The
 * window measures SILENCE, not age — any push saying the child is running
 * refreshes it — so a genuinely long fan-out keeps counting while it is
 * spoken about, and only an abandoned entry ages out.
 *
 * Ten minutes because a sub-agent doing real work runs for minutes, and
 * dropping a live one is the failure this feature is about.
 */
export const SUBTASK_STALE_MS = 10 * 60_000;

/**
 * Upper bound on subtasks tracked per chat, so a gateway that pushes starts
 * without ends cannot grow a bucket for the life of the page. Ends are still
 * processed above the cap, so the count recovers.
 */
export const MAX_TRACKED_SUBTASKS = 99;

/** No subtasks anywhere — the initial state, and what a reconnect returns to. */
export const NO_RUNNING_SUBTASKS: RunningSubtasks = new Map();

function readString(
  source: Record<string, unknown>,
  key: string,
): string | null {
  const value = source[key];
  return typeof value === 'string' && value.length > 0 ? value : null;
}

/**
 * Read a `sessions.changed` payload as a sub-agent subtask update, or null
 * when it is not one.
 */
export function parseSubagentActivity(row: unknown): SubagentActivity | null {
  if (row == null || typeof row !== 'object' || Array.isArray(row)) return null;
  const source = row as Record<string, unknown>;

  // `sessions.describe` calls these `key` and `parentSessionKey`. Those names
  // stay unread until a caller feeds such a row in.
  const key = readString(source, 'sessionKey');
  if (key == null) return null;
  const parentKey = readString(source, 'spawnedBy');

  // A build that echoed the row's own key as its parent link would otherwise
  // count the chat on screen as a subtask running under itself. Exact match
  // only: the namespaced form (`agent:main:<id>` naming `<id>`) needs the
  // caller's key resolver, so the frame handler checks for it too.
  const parent = parentKey === key ? null : parentKey;

  return { key, parentKey: parent, running: readRunning(source) };
}

/**
 * Activity in order of what each field proves: `activeRunIds` is the exact
 * set, so `[]` proves idle; `hasActiveRun` is authoritative but coarse;
 * `status` is the last resort (see `ACTIVE_STATUSES`).
 *
 * `activeRunIds: null` is the tombstone that clears cached ids, not a claim of
 * idleness, so it falls through to the coarser signals.
 */
function readRunning(source: Record<string, unknown>): boolean | null {
  const activeRunIds = source.activeRunIds;
  if (Array.isArray(activeRunIds)) return activeRunIds.length > 0;

  if (typeof source.hasActiveRun === 'boolean') return source.hasActiveRun;

  const status = readString(source, 'status');
  if (status != null) return ACTIVE_STATUSES.has(status.toLowerCase());

  return null;
}

/**
 * `status` values meaning the run is live or about to be.
 *
 * An allow-list, so an unfamiliar value (`error`, `cancelled`, whatever a
 * later build invents) reads as stopped rather than as "no information" and
 * cannot pin a finished child on the count.
 *
 * Unverified, unlike the two signals preferred over it: the one capture in
 * this repo (`session-usage-fixture.ts`) carries `hasActiveRun` and no
 * `status`, so this vocabulary is inference from the protocol notes.
 */
const ACTIVE_STATUSES: ReadonlySet<string> = new Set(['running', 'queued']);

/** One child's state, with its parent resolved to a chat this client knows. */
export interface SubtaskUpdate {
  /** The child's session key. */
  readonly key: string;
  /** The chat it runs under, or null when the row named none. */
  readonly parentId: string | null;
  readonly running: boolean;
}

/**
 * Fold one child's state into the per-chat map, returning `prev` by identity
 * when nothing changed so the caller can skip its `setState`.
 *
 * A start needs `parentId` — a child that names no chat cannot be counted
 * under one. A stop does not: it removes the key wherever it is tracked, so a
 * gateway that omits `spawnedBy` on the end push cannot pin the count.
 *
 * A repeat start refreshes `lastSeenAt`, which is what makes the stale window
 * measure silence rather than age.
 */
export function applySubagentActivity(
  prev: RunningSubtasks,
  update: SubtaskUpdate,
  now: number,
): RunningSubtasks {
  const live = pruneStaleSubtasks(prev, now);
  if (!update.running) return withoutSubtask(live, update.key);
  if (update.parentId == null) return live;

  const bucket = live.get(update.parentId) ?? [];
  const tracked = bucket.find((subtask) => subtask.key === update.key);
  if (tracked?.lastSeenAt === now) return live;
  if (tracked == null && bucket.length >= MAX_TRACKED_SUBTASKS) return live;

  const next = new Map(live);
  next.set(
    update.parentId,
    tracked == null
      ? [...bucket, { key: update.key, lastSeenAt: now }]
      : bucket.map((subtask) =>
          subtask.key === update.key
            ? { ...subtask, lastSeenAt: now }
            : subtask,
        ),
  );
  return next;
}

/** How many subtasks are running under `parentId`. */
export function countRunningSubtasks(
  subtasks: RunningSubtasks,
  parentId: string | null | undefined,
  now: number,
): number {
  if (parentId == null) return 0;
  const bucket = subtasks.get(parentId);
  if (bucket == null) return 0;
  return bucket.filter((subtask) => !isStale(subtask, now)).length;
}

/**
 * Forget one chat's subtasks — for the actions that end its turn (Stop, clear
 * chat), where a count the user cannot see the work behind reads as stuck.
 */
export function clearSubtasksFor(
  subtasks: RunningSubtasks,
  parentId: string | null | undefined,
): RunningSubtasks {
  if (parentId == null || !subtasks.has(parentId)) return subtasks;
  const next = new Map(subtasks);
  next.delete(parentId);
  return next;
}

function isStale(subtask: RunningSubtask, now: number): boolean {
  return now - subtask.lastSeenAt >= SUBTASK_STALE_MS;
}

/** Drop aged-out entries, and any chat left with none. */
export function pruneStaleSubtasks(
  subtasks: RunningSubtasks,
  now: number,
): RunningSubtasks {
  let next: Map<string, readonly RunningSubtask[]> | null = null;
  for (const [parentId, bucket] of subtasks) {
    const live = bucket.filter((subtask) => !isStale(subtask, now));
    if (live.length === bucket.length) continue;
    next ??= new Map(subtasks);
    if (live.length === 0) next.delete(parentId);
    else next.set(parentId, live);
  }
  return next ?? subtasks;
}

function withoutSubtask(
  subtasks: RunningSubtasks,
  key: string,
): RunningSubtasks {
  for (const [parentId, bucket] of subtasks) {
    if (!bucket.some((subtask) => subtask.key === key)) continue;
    const next = new Map(subtasks);
    const live = bucket.filter((subtask) => subtask.key !== key);
    if (live.length === 0) next.delete(parentId);
    else next.set(parentId, live);
    return next;
  }
  return subtasks;
}
